import { authService } from '@/services/auth-service';
import { navigate } from '@/utils/navigate';
import './nu-activity-logs';
import './nu-subscribe';
import './nu-tickets';
import './nu-token-provision';
import './nu-user-menu';

class NuRouter extends HTMLElement {
  private readonly routes: {
    path: string;
    requireAuth?: boolean;
    render: (params: Record<string, string>, query: URLSearchParams) => HTMLElement;
  }[];

  private _onPopstate = () => this._renderRoute();

  constructor() {
    super();
    this.routes = [
      {
        path: "/app/org/new",
        requireAuth: true,
        render: () => document.createElement("nu-org")
      },
      {
        path: "/app/org/{orgId}/scan",
        requireAuth: true,
        render: (params) => {
          const scan = document.createElement("nu-scan");
          scan.setAttribute("org-id", params.orgId);
          return scan;
        }
      },
      {
        path: "/app/org/{orgId}/qr-print",
        requireAuth: true,
        render: (params) => {
          const qr = document.createElement("nu-qr-print");
          qr.setAttribute("org-id", params.orgId);
          return qr;
        }
      },
      {
        path: "/app/org/{orgId}/sub-qr",
        requireAuth: true,
        render: (params) => {
          const el = document.createElement("nu-token-provision");
          el.setAttribute("org-id", params.orgId);
          return el;
        }
      },
      {
        path: "/app/org/{orgId}/tickets",
        requireAuth: true,
        render: (params) => {
          const el = document.createElement("nu-tickets");
          el.setAttribute("org-id", params.orgId);
          return el;
        }
      },
      {
        path: "/app/org/{orgId}/activity-logs",
        requireAuth: true,
        render: (params) => {
          const el = document.createElement("nu-activity-logs");
          el.setAttribute("org-id", params.orgId);
          return el;
        }
      },
      {
        path: "/app/org/{orgId}",
        requireAuth: true,
        render: (params) => {
          const org = document.createElement("nu-org");
          org.setAttribute("org-id", params.orgId);
          return org;
        }
      },
      {
        path: "/app/scan/{accessId}",
        render: (params) => {
          const scan = document.createElement("nu-scan");
          scan.setAttribute("access-id", params.accessId);
          return scan;
        }
      },
      {
        path: "/app/subscribe/{token}",
        render: (params) => {
          const sub = document.createElement("nu-subscribe");
          sub.setAttribute("token", params.token);
          return sub;
        }
      },
      {
        path: "/app/login",
        render: () => document.createElement("nu-login")
      },
      {
        path: "/app",
        requireAuth: true,
        render: () => document.createElement("nu-app-landing")
      }
    ];
  }

  async connectedCallback() {
    await authService.tryRefresh();
    window.addEventListener("popstate", this._onPopstate);
    this._renderRoute();
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this._onPopstate);
  }

  navigate(path: string) {
    history.pushState({}, "", path);
    this._renderRoute();
  }

  private _matchRoute(routePath: string, currentPath: string): Record<string, string> | null {
    const paramNames: string[] = [];
    const regexPath = routePath.replace(/\{([^}]+)}/g, (_, key) => {
      paramNames.push(key);
      return "([^/]+)";
    });
    const regex = new RegExp(`^${regexPath}$`);
    const match = currentPath.match(regex);
    if (!match) return null;
    const params: Record<string, string> = {};
    paramNames.forEach((name, i) => { params[name] = match[i + 1]; });
    return params;
  }

  private _renderRoute(): void {
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const query = new URLSearchParams(window.location.search);

    for (const route of this.routes) {
      const params = this._matchRoute(route.path, currentPath);
      if (!params) continue;

      if (route.requireAuth && !authService.isAuthenticated()) {
        const returnTo = currentPath + (window.location.search || '');
        navigate('/app/login?redirect=' + encodeURIComponent(returnTo));
        return;
      }

      this.style.display = 'block';
      this.replaceChildren(route.render(params, query));
      return;
    }

    // No match — hide and let the landing page show through
    this.style.display = 'none';
    this.replaceChildren();
  }
}

customElements.define("nu-router", NuRouter);